import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { PrepaidAccountTopUpModalConfig as PrepaidAccountTopUpModalContentConfig } from '@front-platform/content-builder';
import { prepaidAccountTopUpModalResolveConfig } from '@front-platform/content-builder';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { ModalScrollBlockBase } from '@core/base/modalScrollBlock/modal-scroll-block.base';
import { GetModalInterface, ModalConfig } from '@core/services/modals/modals-util.type';
import { ModalsService } from '@core/services/modals/modals.service';
import { ModalFillerAtomComponent } from '@shared/atoms/modal-filler-atom/modal-filler-atom.component';
import { map } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { TopUpService } from '../../../core/services/prepayment/top-up.service';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import {
  FreeAmountButtonPosition,
  PrepaymentTopUpComponent,
} from '@shared/components/prepayment-top-up/prepayment-top-up.component';
import { TopUpEvent } from '../../../core/services/prepayment/top-up.directive';
import { PrepaymentUserBalanceBlockComponent } from '@shared/blocks/prepayment-user-balance-block/prepayment-user-balance-block.component';
import { ShopPickerEntities } from '@core/models/shop.model';
import { TranslationField } from '@shared/interfaces/translation-field.interface';
import { TranslateFieldPipe } from '@shared/pipes/translate-field/translate-field.pipe';

export type PrepaidAccountTopUpModalConfig = ModalConfig<{
  handle: typeof PrepaidAccountTopUpModalComponent.handle;
  inputs: {
    shopId: string;
    freeAmountButtonPosition?: FreeAmountButtonPosition;
    description?: TranslationField;
  };
  outputs: TopUpEvent;
}>;
export interface PrepaidAccountTopUpModalRuntimeConfig {
  'shop_id'?: string;
}

export type PrepaidAccountTopUpModalConfigInput = Partial<
  Omit<PrepaidAccountTopUpModalContentConfig, keyof PrepaidAccountTopUpModalRuntimeConfig> &
    PrepaidAccountTopUpModalRuntimeConfig
>;
export type PrepaidAccountTopUpModalResolvedConfig = Omit<
  PrepaidAccountTopUpModalContentConfig,
  keyof PrepaidAccountTopUpModalRuntimeConfig
> &
  Partial<PrepaidAccountTopUpModalRuntimeConfig>;
export interface PrepaidAccountTopUpModalContract extends ConfigComponentContract<
  PrepaidAccountTopUpModalConfigInput,
  PrepaidAccountTopUpModalResolvedConfig
> {}

@Component({
  selector: 'app-prepaid-account-top-up-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalFillerAtomComponent,
    TranslateModule,
    FormsModule,
    PrepaymentTopUpComponent,
    PrepaymentUserBalanceBlockComponent,
    TranslateFieldPipe,
  ],
  providers: [TopUpService],
  template: `
    <app-modal-filler-atom
      (onClick)="close()"
      [index]="index()"
    ></app-modal-filler-atom>
    <div
      class="fixed left-0 right-0 overflow-y-auto shadow md:left-0 md:top-0 md:flex md:h-full md:w-full md:items-center md:justify-center mdMax:mx-auto mdMax:max-h-85-screen mdMax:max-w-screen-sm"
      [style.z-index]="index() + 50"
      [style.bottom.px]="keyboardOffset"
      [class.mdMax:bottom-0]="keyboardOffset === 0"
      role="dialog"
      aria-modal="true"
      [attr.aria-labelledby]="'modal-title-' + index()"
      [attr.aria-describedby]="'modal-description-' + index()"
    >
      @if (isDesktop()) {
        <app-modal-filler-atom
          [withBackdrop]="false"
          (onClick)="close()"
          [index]="index()"
        ></app-modal-filler-atom>
      }
      <div
        class="flex w-full flex-col space-y-4 rounded-t-3xl bg-white p-5 md:min-w-[448px] md:max-w-screen-sm md:rounded-3xl md:p-7"
        [style.z-index]="index() + 50"
      >
        <h2
          [id]="'modal-title-' + index()"
          class="flex items-center text-xl font-bold text-neutral-800 font-accent"
        >
          <i class="icon icon-handout mr-3 text-xl"></i>
          {{ 'prepaid-account-top-up-modal.title' | translate }}
        </h2>
        <app-prepayment-user-balance-block [config]="{ shop_entity: shopEntity() }" />
        @if (description() | translateField; as descriptionText) {
          <p
            class="text-sm text-neutral-600 whitespace-pre-line"
            [id]="'modal-description-' + index()"
          >
            {{ descriptionText }}
          </p>
        }
        <app-prepayment-top-up
          [freeAmountButtonPosition]="freeAmountButtonPosition()"
          (topUpEvents)="onTopUpEvent($event)"
        />
      </div>
    </div>
  `,
})
export class PrepaidAccountTopUpModalComponent
  extends ModalScrollBlockBase
  implements GetModalInterface<PrepaidAccountTopUpModalConfig>, PrepaidAccountTopUpModalContract
{
  readonly config = input<PrepaidAccountTopUpModalConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    PrepaidAccountTopUpModalContentConfig,
    PrepaidAccountTopUpModalRuntimeConfig
  >(prepaidAccountTopUpModalResolveConfig, () => ({ config: this.config() }));
  static handle = 'prepaid-account-top-up-modal' as const;

  private readonly modalsService: ModalsService<PrepaidAccountTopUpModalConfig> =
    inject(ModalsService);
  protected readonly topUpService = inject(TopUpService);
  private readonly settingsStoreSelector = inject(SettingsStoreSelector);

  public readonly index = toSignal(
    this.modalsService
      .getData('prepaid-account-top-up-modal')
      .pipe(map(el => (el?.index ? el.index : 4))),
    { initialValue: 4 },
  );

  public readonly shopId = computed(() => this.resolvedConfig()['shop_id'] as unknown as string);
  public readonly freeAmountButtonPosition = computed(
    () =>
      this.resolvedConfig()['free_amount_button_position'] as unknown as FreeAmountButtonPosition,
  );
  public readonly description = computed(
    () => this.resolvedConfig()['description'] as unknown as TranslationField,
  );

  protected readonly shopEntity = computed<ShopPickerEntities>(() => {
    return [
      {
        id: this.shopId(),
        name: '',
        slug: '',
        position: 0,
      },
    ];
  });

  constructor() {
    super();
  }

  protected readonly isDesktop = toSignal(this.settingsStoreSelector.selectDeviceIsDesktopScreen);

  protected onTopUpEvent(event: TopUpEvent) {
    this.modalsService.emitEvent({
      handle: 'prepaid-account-top-up-modal',
      data: event,
    });
    this.close();
  }

  protected close(): void {
    this.modalsService.close('prepaid-account-top-up-modal');
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(PrepaidAccountTopUpModalComponent, {
  'keyAliases': {},
});
